import { CheckOutlined, DesktopOutlined, MoonOutlined, SunOutlined } from "@ant-design/icons";
import { Dropdown, Segmented } from "antd";

import { useLoc } from "@app/shared/lib/hooks";
import type { ThemeMode } from "@app/shared/lib/theme/theme";
import { useTheme } from "@app/shared/providers/ThemeProvider";

import { ShellIconButton } from "./ShellIconButton";

import styles from "./AppShell.module.scss";

const ICONS: Record<ThemeMode, React.ReactNode> = {
  light: <SunOutlined />,
  dark: <MoonOutlined />,
  system: <DesktopOutlined />,
};

const useThemeOptions = () => {
  const loc = useLoc();
  return [
    { value: "light" as const, label: loc.word("School.theme.light", { default: "Light" }) },
    { value: "dark" as const, label: loc.word("School.theme.dark", { default: "Dark" }) },
    { value: "system" as const, label: loc.word("School.theme.system", { default: "Auto" }) },
  ];
};

/** Light / dark / follow the device. `block`: full width, icon above the label so every name fits. */
export const ThemeSwitch: React.FC<{ block?: boolean }> = ({ block }) => {
  const { mode, setMode } = useTheme();
  const options = useThemeOptions();

  return (
    <Segmented<ThemeMode>
      block={block}
      className={block ? styles.themeSwitchBlock : undefined}
      value={mode}
      onChange={setMode}
      options={options.map((x) => ({ ...x, icon: ICONS[x.value] }))}
    />
  );
};

/**
 * The theme as its own app-bar action (desktop and tablet): the icon shows the current mode, the menu picks another.
 * Phones keep the switch in the account sheet.
 */
export const ThemeMenuButton: React.FC = () => {
  const loc = useLoc();
  const { mode, setMode } = useTheme();
  const options = useThemeOptions();

  return (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      menu={{
        selectable: true,
        selectedKeys: [mode],
        onClick: ({ key }) => setMode(key as ThemeMode),
        items: options.map((x) => ({
          key: x.value,
          icon: ICONS[x.value],
          label: x.label,
          extra: x.value === mode ? <CheckOutlined /> : undefined,
        })),
      }}
    >
      <ShellIconButton icon={ICONS[mode]} label={loc.word("School.theme.title", { default: "Appearance" })} testId="theme-menu" />
    </Dropdown>
  );
};
